import { memo, useContext, useMemo, type ReactNode } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { LogOut, Menu, Moon, PanelLeftClose, PanelLeftOpen, Sun, MonitorSmartphone } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import Logo from './Logo'
import { SettingsContext } from '../../context/settings-context'
import { AuthContext } from '../../context/auth-context'
import { cn } from '../../lib/utils'

type ThemeOption = 'light' | 'dark' | 'auto'

interface HeaderProps {
    appName: string
    onToggleSidebar: () => void
    onToggleMobileSidebar: () => void
    isSidebarCollapsed: boolean
    isMobileSidebarOpen: boolean
    isDesktop: boolean
}

const THEME_OPTIONS: { value: ThemeOption; label: string; description: string; icon: ReactNode }[] = [
    { value: 'auto', label: 'Auto', description: 'Follow system preference', icon: <MonitorSmartphone className="tw:h-4 tw:w-4" /> },
    { value: 'light', label: 'Light', description: 'Bright interface', icon: <Sun className="tw:h-4 tw:w-4" /> },
    { value: 'dark', label: 'Dark', description: 'Low-light mode', icon: <Moon className="tw:h-4 tw:w-4" /> },
]

const headerButtonBase =
    'tw:flex tw:h-10 tw:w-10 tw:items-center tw:justify-center tw:rounded-xl tw:border tw:border-slate-800 tw:bg-slate-900/40 tw:text-slate-300 tw:transition-colors hover:tw:bg-slate-800 hover:tw:text-white focus-visible:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-blue-500'

const buildInitials = (email?: string | null) => {
    if (!email) {
        return 'S'
    }
    return email.charAt(0).toUpperCase()
}

const Header = memo(({ appName, onToggleSidebar, onToggleMobileSidebar, isSidebarCollapsed, isMobileSidebarOpen, isDesktop }: HeaderProps) => {
    const { theme, updateTheme } = useContext(SettingsContext)
    const auth = useContext(AuthContext) as { logout: () => void; user: { email?: string | null } | null }
    const authUser = auth?.user ?? null
    const logout = auth?.logout ?? (() => {})
    const navigate = useNavigate()

    const themeLabel = useMemo(() => {
        const current = THEME_OPTIONS.find((option) => option.value === theme)
        return current?.label ?? 'Auto'
    }, [theme])

    const handleLogout = () => {
        logout()
        navigate('/login')
    }

    const toggleButton = isDesktop ? (
        <button
            type="button"
            onClick={onToggleSidebar}
            className={headerButtonBase}
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!isSidebarCollapsed}
        >
            {isSidebarCollapsed ? <PanelLeftOpen className="tw:h-5 tw:w-5" /> : <PanelLeftClose className="tw:h-5 tw:w-5" />}
        </button>
    ) : (
        <button
            type="button"
            onClick={onToggleMobileSidebar}
            className={headerButtonBase}
            aria-label={isMobileSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
            aria-expanded={isMobileSidebarOpen}
        >
            <Menu className="tw:h-5 tw:w-5" />
        </button>
    )

    return (
        <header className="tw:sticky tw:top-0 tw:z-20 tw:flex tw:h-16 tw:flex-none tw:items-center tw:justify-between tw:border-b tw:border-slate-800 tw:bg-slate-950/80 tw:px-4 tw:backdrop-blur">
            <div className="tw:flex tw:items-center tw:gap-3">
                {toggleButton}
                {!isDesktop && (
                    <Link to="/" className="tw:flex tw:items-center tw:gap-2 tw:text-white tw:no-underline">
                        <Logo />
                        <span className="tw:text-base tw:font-semibold tw:text-white tw:truncate">{appName}</span>
                    </Link>
                )}
            </div>
            <div className="tw:flex tw:items-center tw:gap-2">
                <Popover.Root>
                    <Popover.Trigger asChild>
                        <button
                            type="button"
                            className={cn(headerButtonBase, 'tw:w-auto tw:gap-2 tw:px-3 tw:text-sm tw:font-medium')}
                            aria-label={`Theme: ${themeLabel}`}
                        >
                            {theme === 'dark' ? <Moon className="tw:h-4 tw:w-4" /> : theme === 'light' ? <Sun className="tw:h-4 tw:w-4" /> : <MonitorSmartphone className="tw:h-4 tw:w-4" />}
                            <span className="tw:hidden sm:tw:inline">{themeLabel}</span>
                        </button>
                    </Popover.Trigger>
                    <Popover.Portal>
                        <Popover.Content
                            sideOffset={10}
                            align="end"
                            className="tw:z-50 tw:w-56 tw:rounded-xl tw:border tw:border-slate-800 tw:bg-slate-900 tw:p-3 tw:text-sm tw:text-slate-200 tw:shadow-xl"
                        >
                            <p className="tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-400">Appearance</p>
                            <div className="tw-flex tw:flex-col tw:gap-1">
                                {THEME_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => updateTheme(option.value)}
                                        className={cn(
                                            'tw-flex tw:w-full tw:items-center tw-gap-3 tw:rounded-lg tw-px-3 tw-py-2 tw-text-left tw-transition-colors focus-visible:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-blue-500',
                                            option.value === theme ? 'tw:bg-blue-600/20 tw:text-white' : 'hover:tw:bg-slate-800'
                                        )}
                                        role="menuitemradio"
                                        aria-checked={option.value === theme}
                                    >
                                        <span className="tw-flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:bg-slate-900/50">
                                            {option.icon}
                                        </span>
                                        <span className="tw:flex tw:flex-col tw:text-sm">
                                            {option.label}
                                            <span className="tw-text-xs tw:text-slate-400">{option.description}</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                            <Popover.Arrow className="tw-fill-slate-900" />
                        </Popover.Content>
                    </Popover.Portal>
                </Popover.Root>
                <Popover.Root>
                    <Popover.Trigger asChild>
                        <button
                            type="button"
                            className={cn(headerButtonBase, 'tw:w-auto tw:gap-2 tw:px-3')}
                            aria-haspopup="menu"
                        >
                            <span className="tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-500/20 tw:text-sm tw:font-semibold tw:text-blue-200">
                                {buildInitials(authUser?.email)}
                            </span>
                            <span className="tw:hidden sm:tw:flex sm:tw:flex-col sm:tw:items-start sm:tw:text-left">
                                <span className="tw:text-sm tw:font-medium tw:text-slate-100 tw:max-w-[120px] tw:truncate">
                                    {authUser?.email}
                                </span>
                            </span>
                        </button>
                    </Popover.Trigger>
                    <Popover.Portal>
                        <Popover.Content
                            sideOffset={10}
                            align="end"
                            className="tw:z-50 tw:w-56 tw:space-y-3 tw:rounded-xl tw:border tw:border-slate-800 tw:bg-slate-900 tw:p-3 tw:text-sm tw:text-slate-200 tw:shadow-xl"
                        >
                            <div className="tw-space-y-1">
                                <p className="tw-text-sm tw:font-semibold tw:text-white">Signed in</p>
                                <p className="tw-text-xs tw:text-slate-400">{authUser?.email || 'superadmin@turbamedia.io'}</p>
                            </div>
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="tw:flex tw:w-full tw:flex-row tw:items-center tw:justify-start tw:gap-2 tw:rounded-lg tw:bg-red-500/10 tw:px-3 tw:py-2 tw:text-left tw:text-sm tw:font-medium tw:text-red-200 tw:transition-colors hover:tw:bg-red-500/20 focus-visible:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-red-500"
                            >
                                <LogOut className="tw:h-4 tw:w-4 tw:flex-shrink-0" />
                                <span className="tw:leading-none">Sign out</span>
                            </button>
                            <Popover.Arrow className="tw-fill-slate-900" />
                        </Popover.Content>
                    </Popover.Portal>
                </Popover.Root>
            </div>
        </header>
    )
})

Header.displayName = 'AppHeader'

export default Header
